import React, { useMemo, useState, useId } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { alpha } from '@mui/material/styles';
import {
  ChartContainer,
  ChartEmptyState,
  ChartTooltip,
  InteractiveLegend,
  chartAnimation,
  getSeriesColor,
  normalizeDataKeys,
  useChartTheme,
} from './chartTheme';
import { colors } from '../../design-system';

const InteractiveLineChart = ({
  data = [],
  dataKeys = [],
  title,
  subtitle,
  height = 320,
  showGrid = true,
  showLegend = true,
  showDots = false,
  strokeWidth = 3,
  lineType = 'monotone',
  animated = true,
  xDataKey = 'name',
  valueFormatter,
  allowDecimals = true,
  ...props
}) => {
  const chartId = useId().replace(/:/g, '-');
  const chartTheme = useChartTheme(height);
  const [hiddenKeys, setHiddenKeys] = useState([]);

  const series = useMemo(
    () => normalizeDataKeys(dataKeys, data, [xDataKey]),
    [data, dataKeys, xDataKey],
  );

  const visibleSeries = useMemo(
    () => series.filter((item) => !hiddenKeys.includes(item.dataKey)),
    [hiddenKeys, series],
  );

  const toggleSeries = (dataKey) => {
    setHiddenKeys((current) => (
      current.includes(dataKey)
        ? current.filter((key) => key !== dataKey)
        : [...current, dataKey]
    ));
  };

  if (!data.length || !series.length) {
    return (
      <ChartContainer title={title} subtitle={subtitle}>
        <ChartEmptyState />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={title} subtitle={subtitle}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={chartTheme.margin} {...props}>
          <defs>
            {series.map((item, index) => {
              const color = getSeriesColor(index, item.color);

              return (
                <linearGradient key={item.dataKey} id={`${chartId}-${item.dataKey}-line`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={alpha(color, 0.78)} />
                  <stop offset="100%" stopColor={color} />
                </linearGradient>
              );
            })}
          </defs>

          {showGrid && (
            <CartesianGrid
              stroke={chartTheme.palette.grid}
              strokeDasharray="4 4"
              vertical={false}
            />
          )}

          <XAxis
            dataKey={xDataKey}
            stroke={chartTheme.palette.axis}
            minTickGap={chartTheme.isMobile ? 24 : 16}
            interval="preserveStartEnd"
            tick={{
              fill: chartTheme.palette.textMuted,
              fontSize: chartTheme.axisFontSize,
              fontFamily: chartTheme.fontFamily,
            }}
            tickMargin={8}
          />

          <YAxis
            stroke={chartTheme.palette.axis}
            width={chartTheme.isMobile ? 32 : 44}
            tickCount={chartTheme.yTickCount}
            allowDecimals={allowDecimals}
            tick={{
              fill: chartTheme.palette.textMuted,
              fontSize: chartTheme.axisFontSize,
              fontFamily: chartTheme.fontFamily,
            }}
            tickFormatter={valueFormatter}
          />

          <Tooltip
            cursor={{ stroke: chartTheme.palette.cursor, strokeWidth: 1 }}
            content={<ChartTooltip valueFormatter={valueFormatter} seriesColors={visibleSeries.reduce((acc, item, i) => { acc[item.dataKey] = getSeriesColor(i, item.color); return acc; }, {})} />}
          />

          {visibleSeries.map((item, index) => {
            const color = getSeriesColor(index, item.color);

            return (
              <Line
                key={item.dataKey}
                type={lineType}
                dataKey={item.dataKey}
                name={item.name}
                stroke={`url(#${chartId}-${item.dataKey}-line)`}
                strokeWidth={item.strokeWidth || strokeWidth}
                dot={showDots ? { r: 4, fill: color, stroke: colors.neutral.white, strokeWidth: 2 } : false}
                activeDot={{
                  r: chartTheme.isMobile ? 5 : 6,
                  fill: color,
                  stroke: colors.neutral.white,
                  strokeWidth: 2,
                }}
                animationDuration={animated ? chartAnimation.duration : 0}
                animationBegin={animated ? chartAnimation.begin + (index * chartAnimation.stagger) : 0}
                animationEasing={chartAnimation.easing}
                strokeLinecap="round"
                strokeLinejoin="round"
                connectNulls
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      {showLegend && (
        <InteractiveLegend
          items={series}
          hiddenKeys={hiddenKeys}
          onToggle={toggleSeries}
          isMobile={chartTheme.isMobile}
        />
      )}
    </ChartContainer>
  );
};

export default InteractiveLineChart;
